import React, { useState, useEffect, useRef } from 'react';
import styles from './StoreOrdering.module.css';

/**
 * QuantityInput - Smart numeric input with formatting
 * Only allows digits, auto-formats with visual feedback
 */
const QuantityInput = ({ value, onChange, unit, autoFocus }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [autoFocus]);

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    if ([8, 9, 27, 13, 46, 37, 39].includes(e.keyCode)) return;
    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return;
    // Block non-numeric
    if (e.key < '0' || e.key > '9') {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    const num = parseInt(digits, 10) || 0;
    onChange(Math.min(num, 9999));
  };

  return (
    <div className={styles['quantity-input-wrapper']}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        className={`${styles['qty-input']} ${styles.smart}`}
        value={value || ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="0"
      />
      <span className={styles['quantity-unit']}>{unit}s</span>
    </div>
  );
};

/**
 * QuantityModal - Clean modal for specifying order quantity
 * Listens to keyboard input globally when open
 */
const QuantityModal = ({ product, onConfirm, onClose, initialQuantity = 0 }) => {
  const [quantity, setQuantity] = useState(initialQuantity);

  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity, product]);

  // Global keyboard listener
  useEffect(() => {
    if (!product) return;

    const handleKeyDown = (e) => {
      // Digits 0-9: append to quantity
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        setQuantity(prev => {
          const newQty = prev === 0 ? parseInt(e.key) : parseInt(`${prev}${e.key}`);
          return Math.min(newQty, 9999);
        });
      }
      // Backspace: remove last digit
      else if (e.key === 'Backspace') {
        e.preventDefault();
        setQuantity(prev => Math.floor(prev / 10));
      }
      // Delete or Escape with no quantity: close modal
      else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      // Enter: confirm if valid
      else if (e.key === 'Enter') {
        e.preventDefault();
        if (quantity > 0) {
          onConfirm(product, quantity);
        }
      }
      // Arrow up: increment
      else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQuantity(prev => Math.min(prev + 1, 9999));
      }
      // Arrow down: decrement
      else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQuantity(prev => Math.max(prev - 1, 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [product, quantity, onConfirm, onClose]);

  if (!product) return null;

  const handleIncrement = () => {
    setQuantity(prev => Math.min(prev + 1, 9999));
  };

  const handleDecrement = () => {
    setQuantity(prev => Math.max(prev - 1, 0));
  };

  const handleConfirm = () => {
    if (quantity > 0) {
      onConfirm(product, quantity);
    }
  };

  const estimatedAmount = product.getEstimatedAmount(quantity);
  const estimatedCost = product.getEstimatedCost(quantity);
  const hasDualUnits = product.hasDualUnits();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className={styles['quantity-modal-overlay']} onClick={onClose}>
      <div className={styles['quantity-modal']} onClick={e => e.stopPropagation()}>
        <div className={styles['quantity-modal-header']}>
          <h3>Agregar a Orden</h3>
          <button className={styles['modal-close-btn']} onClick={onClose}>&times;</button>
        </div>

        <div className={styles['quantity-modal-body']}>
          <div className={styles['quantity-product-name']}>{product.name}</div>

          {/* Smart quantity input with +/- buttons */}
          <div className={styles['quantity-input-section']}>
            <button
              className={`${styles['qty-btn']} ${styles.large}`}
              onClick={handleDecrement}
              disabled={quantity <= 0}
            >
              −
            </button>
            <QuantityInput
              value={quantity}
              onChange={setQuantity}
              unit={product.orderUnit}
            />
            <button
              className={`${styles['qty-btn']} ${styles.large}`}
              onClick={handleIncrement}
            >
              +
            </button>
          </div>

          {/* Estimates */}
          <div className={styles['quantity-estimates']}>
            {hasDualUnits && product.estimatedConversion && (
              <div className={styles['estimate-row']}>
                <span className={styles['estimate-label']}>Estimado:</span>
                <span className={styles['estimate-value']}>~{estimatedAmount.toFixed(0)} {product.pricingUnit}</span>
              </div>
            )}
            <div className={`${styles['estimate-row']} ${styles.total}`}>
              <span className={styles['estimate-label']}>Total</span>
              <span className={styles['estimate-value']}>{hasDualUnits ? '~' : ''}{formatCurrency(estimatedCost)}</span>
            </div>
          </div>
        </div>

        <div className={styles['quantity-modal-footer']}>
          <button
            className={styles['add-to-order-btn']}
            onClick={handleConfirm}
            disabled={quantity <= 0}
          >
            Agregar a Orden
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuantityModal;
